/*
PURPOSE: Autocomplete dropdown for @ mention suggestions in comments

USE CASES:
- Display list of aligned users when @ is typed
- Allow keyboard navigation through suggestions
- Handle mention selection
- Position dropdown relative to cursor

DOMAIN BOUNDARIES:
- Responsible for autocomplete UI rendering only
- Does NOT handle mention detection (handled by useMentionDetection hook)
- Does NOT make API calls directly
- Does NOT manage text input state

ASSUMPTIONS:
- Receives suggestions from parent component via useMentionDetection hook
- Keyboard navigation (↑↓ Enter Esc) handled by parent
- Dropdown positioned absolutely relative to container
- Mobile-friendly with touch interactions
*/

import React, { useRef, useEffect } from 'react';
import { MentionSuggestion } from '../../../hooks/useMentionDetection';
import Spinner from '../../Spinner/Spinner';

interface UserMentionAutocompleteProps {
  isOpen: boolean;
  suggestions: MentionSuggestion[];
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (suggestion: MentionSuggestion) => void;
  className?: string;
}

const UserMentionAutocomplete: React.FC<UserMentionAutocompleteProps> = ({
  isOpen,
  suggestions,
  isLoading,
  selectedIndex,
  onSelect,
  className
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={
        className ||
        'absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50'
      }
    >
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Spinner className="h-4 w-4" />
          <span className="ml-2 text-sm text-gray-500">Searching...</span>
        </div>
      )}

      {!isLoading && suggestions.length === 0 && (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          No aligned users found
        </div>
      )}

      {!isLoading && suggestions.length > 0 && (
        <ul className="py-1">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.userId}
              onClick={() => onSelect(suggestion)}
              className={`px-4 py-2 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? 'bg-coral-pink/10 text-coral-pink'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {suggestion.displayName}
                  </div>
                  {suggestion.firstName && (
                    <div className="text-xs text-gray-500">
                      @{suggestion.displayName}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default UserMentionAutocomplete;
